import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { AthleteCombobox } from './AthleteCombobox';
import { superfightApi, athleteApi } from '../lib/api';
import { toast } from 'sonner';
import { EstadoVazio } from './common/Estados';
import { Swords, Loader2, Plus, Trash2, Play, CheckCircle } from 'lucide-react';

/**
 * Lutas Casadas (Superfights) — single one-off matches manually paired,
 * ignoring weight/age/belt/gender constraints. Lives in its own collection,
 * so it doesn't pollute the regular bracket structure.
 */
export function SuperfightsTab({ championshipId }) {
  const [list, setList] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    label: '',
    athlete1_id: '',
    athlete2_id: '',
    scheduled_at: '',
    duration_minutes: 6,
    notes: '',
  });
  const [resultDialog, setResultDialog] = useState(null);
  const [resultForm, setResultForm] = useState({ winner_id: '', result_type: 'points', score1: 0, score2: 0 });
  const [savingResult, setSavingResult] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [sfRes, athRes] = await Promise.all([
        superfightApi.list(championshipId),
        athleteApi.list(),
      ]);
      setList(sfRes.data || []);
      setAthletes(athRes.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao carregar lutas casadas');
    } finally {
      setLoading(false);
    }
  }, [championshipId]);

  useEffect(() => { reload(); }, [reload]);

  const athleteLookup = useMemo(() => {
    const m = new Map();
    for (const a of athletes) m.set(a._id, a);
    return m;
  }, [athletes]);

  const handleCreate = async () => {
    if (!form.label.trim()) { toast.error('Informe um título'); return; }
    if (!form.athlete1_id || !form.athlete2_id) { toast.error('Selecione os dois atletas'); return; }
    if (form.athlete1_id === form.athlete2_id) { toast.error('Os atletas devem ser diferentes'); return; }
    setCreating(true);
    try {
      await superfightApi.create({
        championship_id: championshipId,
        label: form.label.trim(),
        athlete1_id: form.athlete1_id,
        athlete2_id: form.athlete2_id,
        scheduled_at: form.scheduled_at || null,
        duration_minutes: form.duration_minutes || null,
        notes: form.notes || null,
      });
      toast.success('Luta casada criada');
      setShowCreate(false);
      setForm({ label: '', athlete1_id: '', athlete2_id: '', scheduled_at: '', duration_minutes: 6, notes: '' });
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao criar luta casada');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (sf) => {
    if (!window.confirm(`Excluir a luta casada "${sf.label}"?`)) return;
    try {
      await superfightApi.remove(sf._id);
      toast.success('Luta excluída');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao excluir');
    }
  };

  const openResult = (sf) => {
    setResultDialog(sf);
    setResultForm({ winner_id: '', result_type: 'points', score1: 0, score2: 0 });
  };

  const handleRecordResult = async () => {
    if (!resultDialog) return;
    if (!resultForm.winner_id && resultForm.result_type !== 'draw') {
      toast.error('Selecione o vencedor');
      return;
    }
    setSavingResult(true);
    try {
      await superfightApi.recordResult(resultDialog._id, resultForm);
      toast.success('Resultado registrado');
      setResultDialog(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao registrar resultado');
    } finally {
      setSavingResult(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="superfights-tab">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Swords className="w-6 h-6 text-[#D4AF37]" />
          <div>
            <h2 className="text-xl font-bold text-white font-['Chivo']">Lutas Casadas</h2>
            <p className="text-sm text-[#71717A]">
              Superlutas avulsas — peso, faixa, idade e gênero livres
              {' · '}{list.length} cadastradas
            </p>
          </div>
        </div>
        <Button
          onClick={() => setShowCreate(true)}
          size="sm"
          className="bg-[#D4AF37] text-black hover:bg-[#E5C158]"
          data-testid="superfight-new"
        >
          <Plus size={16} className="mr-1" /> Nova Luta Casada
        </Button>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" aria-hidden="true" /><span role="status" aria-live="polite" className="ml-2 text-sm text-[#A1A1AA]">Carregando...</span>
        </div>
      ) : list.length === 0 ? (
        <Card className="bg-[#141414] border-[#27272A]">
          <CardContent>
            <EstadoVazio
              icone={Swords}
              titulo="Nenhuma luta casada cadastrada"
              descricao="Crie superlutas avulsas como exibições, desafios ou homenagens"
            />
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {list.map((sf) => {
            const a1Winner = sf.winner_id && sf.winner_id === sf.athlete1_id;
            const a2Winner = sf.winner_id && sf.winner_id === sf.athlete2_id;
            return (
              <Card key={sf._id} className="bg-[#141414] border-[#27272A]" data-testid={`superfight-${sf._id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-[#D4AF37]">{sf.code}</span>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        sf.status === 'finished' ? 'status-finished' :
                        sf.status === 'in_progress' ? 'status-in_progress' : 'status-open'
                      }`}>
                        {sf.status === 'finished' ? 'Finalizada' : sf.status === 'in_progress' ? 'Em andamento' : 'Agendada'}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDelete(sf)}
                      className="text-[#71717A] hover:text-red-400"
                      data-testid={`superfight-delete-${sf._id}`}
                      title="Excluir"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>

                  <div className="text-white font-medium mb-2">{sf.label}</div>

                  <div className="flex items-center gap-2 mb-3">
                    <div className={`flex-1 p-2 rounded ${a1Winner ? 'bg-green-500/15 border border-green-500/40' : 'bg-[#1A1A1A]'}`}>
                      <div className="text-sm text-white truncate">{sf.athlete1_name}</div>
                      <div className="text-xs text-[#71717A] truncate">
                        {sf.athlete1_belt} · {sf.athlete1_weight ? `${sf.athlete1_weight}kg` : ''} · {sf.athlete1_team}
                      </div>
                    </div>
                    <span className="text-[#71717A] text-xs font-bold">VS</span>
                    <div className={`flex-1 p-2 rounded ${a2Winner ? 'bg-green-500/15 border border-green-500/40' : 'bg-[#1A1A1A]'}`}>
                      <div className="text-sm text-white truncate">{sf.athlete2_name}</div>
                      <div className="text-xs text-[#71717A] truncate">
                        {sf.athlete2_belt} · {sf.athlete2_weight ? `${sf.athlete2_weight}kg` : ''} · {sf.athlete2_team}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-xs text-[#71717A] mb-2">
                    <span>
                      {sf.scheduled_at && new Date(sf.scheduled_at).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
                      {sf.duration_minutes && ` · ${sf.duration_minutes} min`}
                    </span>
                    {sf.status === 'finished' && (
                      <span className="text-green-500 flex items-center gap-1">
                        <CheckCircle size={12} /> {sf.score1}-{sf.score2}
                      </span>
                    )}
                  </div>

                  {sf.status !== 'finished' && (
                    <Button
                      onClick={() => openResult(sf)}
                      size="sm"
                      className="w-full bg-[#D4AF37] text-black hover:bg-[#E5C158]"
                      data-testid={`superfight-record-${sf._id}`}
                    >
                      <Play size={16} className="mr-1" /> Registrar Resultado
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* CREATE DIALOG */}
      <Dialog open={showCreate} onOpenChange={(o) => !o && !creating && setShowCreate(false)}>
        <DialogContent className="bg-[#141414] border-[#27272A] max-w-md" data-testid="superfight-create-dialog">
          <DialogHeader>
            <DialogTitle className="text-white font-['Chivo']">Nova Luta Casada</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="space-y-1.5">
              <Label className="text-[#A1A1AA]">Título *</Label>
              <Input
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="Superluta Faixa Preta vs Marrom"
                className="bg-[#1A1A1A] border-[#27272A] text-white"
                data-testid="superfight-form-label"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="text-[#A1A1AA]">Atleta 1 *</Label>
                <AthleteCombobox
                  athletes={athletes}
                  value={form.athlete1_id}
                  onChange={(v) => setForm({ ...form, athlete1_id: v })}
                  excludeId={form.athlete2_id}
                  testId="superfight-form-a1"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#A1A1AA]">Atleta 2 *</Label>
                <AthleteCombobox
                  athletes={athletes}
                  value={form.athlete2_id}
                  onChange={(v) => setForm({ ...form, athlete2_id: v })}
                  excludeId={form.athlete1_id}
                  testId="superfight-form-a2"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="text-[#A1A1AA]">Horário</Label>
                <Input
                  type="datetime-local"
                  value={form.scheduled_at}
                  onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
                  className="bg-[#1A1A1A] border-[#27272A] text-white"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#A1A1AA]">Duração (min)</Label>
                <Input
                  type="number"
                  min="1"
                  max="30"
                  value={form.duration_minutes}
                  onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value) || null })}
                  className="bg-[#1A1A1A] border-[#27272A] text-white"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-[#A1A1AA]">Observações</Label>
              <Input
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Regras especiais, narrativa, etc."
                className="bg-[#1A1A1A] border-[#27272A] text-white"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowCreate(false)} disabled={creating} className="border-[#27272A] text-[#A1A1AA]">
                Cancelar
              </Button>
              <Button onClick={handleCreate} disabled={creating} className="bg-[#D4AF37] text-black hover:bg-[#E5C158]" data-testid="superfight-form-save">
                {creating ? <Loader2 size={16} className="mr-1 animate-spin" /> : null} Criar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* RESULT DIALOG */}
      <Dialog open={!!resultDialog} onOpenChange={(o) => !o && !savingResult && setResultDialog(null)}>
        <DialogContent className="bg-[#141414] border-[#27272A] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white font-['Chivo']">Registrar Resultado</DialogTitle>
          </DialogHeader>
          {resultDialog && (
            <div className="space-y-3 mt-2">
              <div className="text-sm text-[#A1A1AA]">{resultDialog.label}</div>
              <div className="space-y-1.5">
                <Label className="text-[#A1A1AA]">Vencedor *</Label>
                <Select value={resultForm.winner_id} onValueChange={(v) => setResultForm({ ...resultForm, winner_id: v })}>
                  <SelectTrigger className="bg-[#1A1A1A] border-[#27272A] text-white" data-testid="superfight-result-winner">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                    <SelectItem value={resultDialog.athlete1_id} className="text-white">{resultDialog.athlete1_name}</SelectItem>
                    <SelectItem value={resultDialog.athlete2_id} className="text-white">{resultDialog.athlete2_name}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#A1A1AA]">Tipo</Label>
                <Select value={resultForm.result_type} onValueChange={(v) => setResultForm({ ...resultForm, result_type: v })}>
                  <SelectTrigger className="bg-[#1A1A1A] border-[#27272A] text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                    <SelectItem value="points" className="text-white">Pontos</SelectItem>
                    <SelectItem value="submission" className="text-white">Finalização</SelectItem>
                    <SelectItem value="advantage" className="text-white">Vantagem</SelectItem>
                    <SelectItem value="decision" className="text-white">Decisão</SelectItem>
                    <SelectItem value="wo" className="text-white">W.O.</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {resultForm.result_type === 'points' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-[#A1A1AA] text-xs">{resultDialog.athlete1_name?.split(' ')[0]}</Label>
                    <Input type="number" min="0" value={resultForm.score1}
                      onChange={(e) => setResultForm({ ...resultForm, score1: parseInt(e.target.value) || 0 })}
                      className="bg-[#1A1A1A] border-[#27272A] text-white" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[#A1A1AA] text-xs">{resultDialog.athlete2_name?.split(' ')[0]}</Label>
                    <Input type="number" min="0" value={resultForm.score2}
                      onChange={(e) => setResultForm({ ...resultForm, score2: parseInt(e.target.value) || 0 })}
                      className="bg-[#1A1A1A] border-[#27272A] text-white" />
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setResultDialog(null)} disabled={savingResult} className="border-[#27272A] text-[#A1A1AA]">
                  Cancelar
                </Button>
                <Button onClick={handleRecordResult} disabled={savingResult} className="bg-[#D4AF37] text-black hover:bg-[#E5C158]" data-testid="superfight-result-save">
                  {savingResult ? <Loader2 size={16} className="mr-1 animate-spin" /> : null} Confirmar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default SuperfightsTab;
